import {
  amountFor,
  baseCodes,
  buildSampleComponents,
  checkFormula,
  codesIn,
  currentVersion,
  describeCalculation,
  formatRupees,
  versionLabel,
} from "../SalaryComponents/salaryComponentData";

// Template rules shared by Salary Templates (on the API, salaryTemplateApi.js)
// and Assign Salary / Salary Revision (still on the sample data below).
// Lines from the API carry their own name, category, component_id and
// default calculation; sample lines are looked up in the sample catalog.

export const PAY_BASES = ["Monthly", "Weekly", "Daily"];
export const PAY_BASIS_TONES = { Monthly: "green", Weekly: "purple", Daily: "blue" };
export const PAY_BASIS_OPTIONS = [
  { value: "Monthly", label: "Monthly", helper: "Fixed amounts per month" },
  { value: "Weekly", label: "Weekly", helper: "Fixed amounts per week" },
  { value: "Daily", label: "Daily", helper: "Fixed amounts per day" },
];
export const PERIOD = { Monthly: "month", Weekly: "week", Daily: "day" };
export const DEFAULT_GROSS = { Monthly: 30000, Weekly: 7000, Daily: 1000 };
export const TEMPLATE_METHODS = ["Fixed Amount", "Percentage", "Formula"];

// Template sections, in payslip order (the old template screen's
// categories). Pay Input and Statutory components are still added at
// payroll, so the server leaves them out of the picker.
//   Earnings               make up GROSS (one of them balances it)
//   Additional Earnings    paid on top of GROSS ("Other Earnings")
//   Deductions             taken off GROSS
//   Additional Deductions  taken off after the Deductions ("Other Deduction")
//   Employer Contribution  paid by the employer: CTC only
//   Reimbursement          paid outside the salary
//   Calculation            helper values formulas read; nobody is paid
export const TEMPLATE_CATEGORIES = [
  "Earnings",
  "Additional Earnings",
  "Deductions",
  "Additional Deductions",
  "Employer Contribution",
  "Reimbursement",
  "Calculation",
];
// Earnings and Deductions always show; the others show once the company has
// a component in them or the template has a row.
export const OPTIONAL_CATEGORIES = TEMPLATE_CATEGORIES.slice(1).filter((category) => category !== "Deductions");
// Singular / plural for the picker and counts.
export const CATEGORY_NOUNS = {
  Earnings: ["earning", "earnings"],
  "Additional Earnings": ["additional earning", "additional earnings"],
  Deductions: ["deduction", "deductions"],
  "Additional Deductions": ["additional deduction", "additional deductions"],
  "Employer Contribution": ["employer contribution", "employer contributions"],
  Reimbursement: ["reimbursement", "reimbursements"],
  Calculation: ["calculation", "calculations"],
};

export const sampleCatalog = () => buildSampleComponents();

export const catalogItem = (catalog, code) => catalog.find((item) => item.code === code);
const componentVersion = (catalog, code, version) =>
  catalogItem(catalog, code)?.versions.find((item) => item.version === version);
export const latestVersionNumber = (catalog, code) => {
  const item = catalogItem(catalog, code);
  return item ? currentVersion(item).version : null;
};

const CALC_KEYS = ["method", "amount", "percentage", "base", "formula"];
const pickCalc = (source) => ({
  method: source.method,
  amount: source.method === "Fixed Amount" ? Number(source.amount) : "",
  percentage: source.method === "Percentage" ? Number(source.percentage) : "",
  base: source.method === "Percentage" ? source.base || "GROSS" : "GROSS",
  formula: source.method === "Formula" ? String(source.formula || "").trim() : "",
});

// A template line: one component, the component version it is pinned to,
// and the calculation the template uses (the version's default unless
// overridden).
export const lineFromComponent = (catalog, code, version) => {
  const item = catalogItem(catalog, code);
  const pinned =
    componentVersion(catalog, code, version ?? latestVersionNumber(catalog, code)) ||
    componentVersion(catalog, code, latestVersionNumber(catalog, code));
  const calc = pickCalc(pinned);
  return {
    code,
    component_id: item?.id,
    name: item?.name || code,
    category: item?.category || "",
    component_version: pinned.version,
    ...calc,
    balancing: Boolean(item?.balancing),
    default: { ...calc },
  };
};

// The pinned component version's own calculation: the catalog's version
// (sample data) or the default the API sent with the line.
export const lineDefault = (catalog, line) =>
  componentVersion(catalog, line.code, line.component_version) || line.default || null;

export const isOverridden = (catalog, line) => {
  if (line.balancing) return false;
  const base = lineDefault(catalog, line);
  if (!base) return line.overridden === true;
  const a = pickCalc(line);
  const b = pickCalc(base);
  return CALC_KEYS.some((key) => String(a[key]) !== String(b[key]));
};

export const lineCategory = (catalog, line) => catalogItem(catalog, line.code)?.category || line.category || "";
export const lineName = (catalog, line) => catalogItem(catalog, line.code)?.name || line.name || line.code;

// The balancing earning always makes earnings add up to GROSS: its formula
// is GROSS minus every other earning in the template.
export const balancingFormula = (catalog, lines, ownCode) => {
  const others = lines
    .filter((line) => line.code !== ownCode && lineCategory(catalog, line) === "Earnings")
    .map((line) => line.code);
  return others.length ? `GROSS - (${others.join(" + ")})` : "GROSS";
};

// Percentage components (Basic is always one) keep their percentage: they
// can never be the balancing earning.
export const canBalance = (catalog, line) => {
  if (String(line.code).toUpperCase() === "BASIC") return false;
  const base = lineDefault(catalog, line);
  return (base?.method || line.method) !== "Percentage";
};

// A line that may not balance, back on its own default calculation.
const releaseBalancing = (catalog, line) => {
  const base = lineDefault(catalog, line);
  return base ? { ...line, ...pickCalc(base), balancing: false } : { ...line, balancing: false };
};

// Drops a balancing flag that sits on a line that can't balance (templates
// saved before this rule had Basic as their balancing row).
export const normalizeBalancing = (catalog, lines) =>
  lines.map((line) => (line.balancing && !canBalance(catalog, line) ? releaseBalancing(catalog, line) : line));

// Lines with the balancing formula filled in.
export const withBalancing = (catalog, lines) => {
  const normalized = normalizeBalancing(catalog, lines);
  return normalized.map((line) =>
    line.balancing ? { ...line, method: "Formula", formula: balancingFormula(catalog, normalized, line.code) } : line,
  );
};

export const describeLine = (line) =>
  line.method === "Fixed Amount" ? `Fixed ${formatRupees(line.amount)}` : describeCalculation(line);

// Code -> amount for every line at the given GROSS. Lines that never
// resolve (circular or missing references) are reported.
// Same totals as the server and the payslip: NET = GROSS + Additional
// Earnings - Deductions - Additional Deductions, CTC = GROSS + Additional
// Earnings + Employer Contribution. Reimbursement and Calculation rows are
// not part of either.
export const evaluateTemplate = (catalog, rawLines, gross) => {
  const lines = withBalancing(catalog, rawLines);
  const values = { GROSS: gross, EGS: gross };
  for (let pass = 0; pass <= lines.length; pass += 1) {
    let changed = false;
    lines.forEach((line) => {
      if (values[line.code] != null) return;
      const amount = amountFor(line, values);
      if (amount != null && Number.isFinite(amount)) {
        values[line.code] = Math.round(amount * 100) / 100;
        changed = true;
      }
    });
    if (!changed) break;
  }
  const sum = (category) =>
    lines
      .filter((line) => lineCategory(catalog, line) === category)
      .reduce((total, line) => total + (values[line.code] || 0), 0);
  const round = (value) => Math.round(value * 100) / 100;
  const earnings = round(sum("Earnings"));
  const otherEarnings = round(sum("Additional Earnings"));
  const deductions = round(sum("Deductions"));
  const otherDeductions = round(sum("Additional Deductions"));
  const employer = round(sum("Employer Contribution"));
  return {
    values,
    earnings,
    otherEarnings,
    deductions,
    otherDeductions,
    employer,
    reimbursement: round(sum("Reimbursement")),
    net: round(earnings + otherEarnings - deductions - otherDeductions),
    ctc: round(earnings + otherEarnings + employer),
    unresolved: lines.filter((line) => values[line.code] == null).map((line) => line.code),
    balanced: Math.abs(earnings - gross) < 0.01,
  };
};

// Codes a line's percentage may be based on: GROSS and the template's
// other Earnings / Additional Earnings (the server's rule).
export const allowedCodes = (catalog, lines, ownCode) => [
  ...lines
    .filter((line) => line.code !== ownCode && ["Earnings", "Additional Earnings"].includes(lineCategory(catalog, line)))
    .map((line) => line.code),
  "GROSS",
];

// Codes a line's formula may use: GROSS and every other row of the template
// (an Employer PF on BASIC, a deduction on a Calculation row, ...).
export const formulaCodes = (lines, ownCode) => [...lines.filter((line) => line.code !== ownCode).map((line) => line.code), "GROSS"];

export const lineErrors = (catalog, lines) => {
  const errors = {};
  lines.forEach((line) => {
    if (line.balancing) return;
    if (line.method === "Fixed Amount" && !(Number(line.amount) > 0)) errors[line.code] = "Enter an amount above 0";
    if (line.method === "Percentage") {
      const pct = Number(line.percentage);
      if (!(pct > 0 && pct <= 100)) errors[line.code] = "Enter a percentage between 0 and 100";
      else {
        const allowed = allowedCodes(catalog, lines, line.code);
        const codes = baseCodes(line.base);
        const missing = codes.filter((code) => !allowed.includes(code));
        if (!codes.length) errors[line.code] = "Select a base";
        else if (missing.length) errors[line.code] = `${missing.join(", ")} ${missing.length === 1 ? "isn't" : "aren't"} in this template`;
      }
    }
    if (line.method === "Formula") {
      const check = checkFormula(line.formula, formulaCodes(lines, line.code));
      if (!check.valid) errors[line.code] = check.message;
    }
  });
  return errors;
};

// ---------- Sample templates ----------

const L = (code, version, override) => ({ code, version, override });

const TEMPLATE_DEFS = [
  {
    name: "Staff — Monthly", description: "Office and support staff", pay_basis: "Monthly", gross: 35000,
    versions: [
      { at: "2024-04-01", by: "Sadheesh", note: "Template created.", lines: [L("BASIC", 1), L("HRA", 1), L("CONV", 1), L("SPL", 1)] },
      { at: "2024-09-18", by: "Sadheesh", note: "Dearness allowance added.", lines: [L("BASIC", 1), L("HRA", 1), L("DA", 1), L("CONV", 1), L("SPL", 2)] },
      { at: "2025-04-10", by: "Sadheesh", note: "Basic moved to % of GROSS. Medical allowance added.", lines: [L("BASIC", 2), L("HRA", 1), L("DA", 1), L("CONV", 1), L("MED", 1), L("SPL", 3)] },
      { at: "2026-09-02", by: "Priya R (HR Manager)", note: "Basic upgraded to 50% of GROSS. Canteen deduction added.", lines: [L("BASIC", 3), L("HRA", 1), L("DA", 1), L("CONV", 1), L("MED", 1), L("SPL", 3), L("CANT", 1)] },
    ],
    employees: { 4: 112 },
  },
  {
    name: "Operators — Monthly", description: "Machine operators on monthly pay", pay_basis: "Monthly", gross: 28000,
    versions: [
      { at: "2024-04-01", by: "Sadheesh", note: "Template created.", lines: [L("BASIC", 2, { method: "Percentage", percentage: 45, base: "GROSS" }), L("HRA", 1), L("DA", 1), L("CONV", 1), L("SPL", 3)] },
      { at: "2026-09-04", by: "Priya R (HR Manager)", note: "Basic upgraded to V3 (kept at 45%). Canteen deduction added.", lines: [L("BASIC", 3, { method: "Percentage", percentage: 45, base: "GROSS" }), L("HRA", 1), L("DA", 1), L("CONV", 1), L("SPL", 3), L("CANT", 1)] },
    ],
    employees: { 2: 64 },
  },
  {
    name: "Supervisors — Monthly", description: "Line and shift supervisors", pay_basis: "Monthly", gross: 40000,
    versions: [
      { at: "2024-04-01", by: "Sadheesh", note: "Template created.", lines: [L("BASIC", 1), L("HRA", 1), L("CONV", 1), L("MED", 1)] },
      { at: "2025-04-10", by: "Sadheesh", note: "Basic moved to % of GROSS. Special Allowance added as the balancing component.", lines: [L("BASIC", 2), L("HRA", 1), L("DA", 1), L("CONV", 1), L("MED", 1), L("SPL", 3)] },
      { at: "2026-06-02", by: "Priya R (HR Manager)", note: "Added Canteen Deduction (₹450). Removed Medical Allowance.", lines: [L("BASIC", 2), L("HRA", 1), L("DA", 1), L("CONV", 1), L("SPL", 3), L("CANT", 1, { method: "Fixed Amount", amount: 450 })] },
    ],
    employees: { 3: 31, 2: 7 },
  },
  {
    name: "Contract Staff — Weekly", description: "Short-term contract workers", pay_basis: "Weekly", gross: 7000,
    versions: [
      { at: "2025-11-03", by: "Priya R (HR Manager)", note: "Template created.", lines: [L("BASIC", 3), L("DA", 1), L("SPL", 3), L("CANT", 1, { method: "Fixed Amount", amount: 120 })] },
    ],
    employees: { 1: 22 },
  },
  {
    name: "Shift Workers — Daily", description: "Daily-rated production staff", pay_basis: "Daily", gross: 1000,
    versions: [
      { at: "2024-06-12", by: "Sadheesh", note: "Template created.", lines: [L("BASIC", 2), L("DA", 1), L("FOOD", 1), L("SPL", 3)] },
      { at: "2026-09-05", by: "Priya R (HR Manager)", note: "Basic upgraded to V3. Canteen deduction added.", lines: [L("BASIC", 3), L("DA", 1), L("FOOD", 1), L("SPL", 3), L("CANT", 1, { method: "Fixed Amount", amount: 20 })] },
    ],
    employees: { 2: 18 },
  },
  {
    name: "Drivers — Daily", description: "Delivery and staff transport drivers", pay_basis: "Daily", gross: 900,
    versions: [
      { at: "2025-08-01", by: "Sadheesh", note: "Template created.", lines: [L("BASIC", 2), L("DA", 1), L("FOOD", 1), L("SPL", 3), L("CANT", 1, { method: "Fixed Amount", amount: 20 })] },
    ],
    employees: { 1: 16 },
  },
  {
    name: "Trainees — Monthly", description: "Graduate and apprentice trainees", pay_basis: "Monthly", gross: 18000,
    versions: [
      { at: "2026-07-20", by: "Priya R (HR Manager)", note: "Template created.", lines: [L("BASIC", 3), L("HRA", 1), L("SPL", 3), L("CANT", 1)] },
    ],
    employees: {},
  },
  {
    name: "Interns — Monthly", description: "Stipend-only interns", pay_basis: "Monthly", gross: 12000,
    versions: [
      { at: "2026-08-11", by: "Priya R (HR Manager)", note: "Template created.", lines: [L("BASIC", 3), L("SPL", 3)] },
    ],
    employees: {},
  },
];

const FIRST_NAMES = [
  "Sathish V", "Revathi N", "Prakash K", "Lakshmi R", "Vignesh S", "Anitha M", "Senthil A", "Kavya P",
  "Arun Kumar", "Meena Lakshmi", "Karthik Raja", "Divya S", "Murugan P", "Ganesh M", "Deepa M", "Hari Prasad",
];
const DEPARTMENTS = ["Production", "Stores", "Quality", "Dyeing", "Packing", "Maintenance", "Logistics"];
const EFFECTIVE_BY_AGE = ["2026-07-01", "2025-04-01", "2024-04-01"];

const buildEmployees = (id, def, versionCount) => {
  const rows = [];
  let serial = 0;
  Object.entries(def.employees)
    .sort(([a], [b]) => Number(a) - Number(b))
    .forEach(([version, count]) => {
      for (let i = 0; i < count; i += 1) {
        serial += 1;
        const seed = (id * 37 + serial * 11) % 991;
        const step = def.pay_basis === "Daily" ? 50 : def.pay_basis === "Weekly" ? 250 : 1000;
        rows.push({
          id: `${id}-${serial}`,
          name: FIRST_NAMES[(serial - 1) % FIRST_NAMES.length],
          employee_code: `EMP-${String(800 + ((seed * 17) % 700)).padStart(4, "0")}`,
          department: DEPARTMENTS[seed % DEPARTMENTS.length],
          version: Number(version),
          customized: serial % 9 === 3,
          gross: def.gross + ((seed % 5) - 2) * step,
          effective_from: EFFECTIVE_BY_AGE[Math.min(versionCount - Number(version), EFFECTIVE_BY_AGE.length - 1)],
        });
      }
    });
  return rows;
};

export const buildSampleTemplates = (catalog) =>
  TEMPLATE_DEFS.map((def, index) => {
    const id = index + 1;
    const versions = def.versions.map((item, versionIndex) => ({
      version: versionIndex + 1,
      created_at: item.at,
      created_by: item.by,
      note: item.note,
      lines: item.lines.map(({ code, version, override }) => ({ ...lineFromComponent(catalog, code, version), ...(override || {}) })),
    }));
    return {
      id,
      name: def.name,
      description: def.description,
      pay_basis: def.pay_basis,
      active: true,
      deactivation: null,
      versions,
      employees: buildEmployees(id, def, versions.length),
      activity: [{ title: "Created as V1", at: versions[0].created_at, by: versions[0].created_by }],
    };
  });

// ---------- Derived values ----------

export const currentTemplateVersion = (template) => template.versions[template.versions.length - 1];

// Employees on a template: the server's count, else the loaded list.
export const templateEmployeeCount = (template) => template.employee_count ?? template.employees.length;

// Used / Unused / Inactive: the server's status when it sends one.
export const templateStatus = (template) => {
  if (!template.active) return "Inactive";
  if (["Used", "Unused"].includes(template.status)) return template.status;
  return templateEmployeeCount(template) ? "Used" : "Unused";
};

// Lines whose component has a newer version: the server's upgrade_available
// / latest_version, else the catalog's latest.
export const componentUpdates = (catalog, lines) =>
  lines
    .map((line) => ({ line, latest: line.latest_version ?? latestVersionNumber(catalog, line.code) }))
    .filter(({ line, latest }) => (latest && latest > line.component_version) || (line.upgrade_available && !line.upgrade))
    .map(({ line, latest }) => ({ line, latest: latest && latest > line.component_version ? latest : line.component_version + 1 }));

// Preview gross: the gross the template was saved at, else the median of
// the employees' gross, else the default for its pay basis.
export const medianGross = (template, defaults = DEFAULT_GROSS) => {
  if (template?.preview_gross > 0) return template.preview_gross;
  const grosses = (template?.employees || []).map((row) => row.gross).filter((value) => Number.isFinite(value));
  if (grosses.length) {
    const sorted = [...grosses].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }
  return defaults[template?.pay_basis] || DEFAULT_GROSS[template?.pay_basis] || DEFAULT_GROSS.Monthly;
};

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// "5 earnings · 1 deduction · 1 employer contribution", in section order.
export const categoryCounts = (catalog, lines) =>
  TEMPLATE_CATEGORIES.map((category) => {
    const count = lines.filter((line) => lineCategory(catalog, line) === category).length;
    if (!count) return null;
    const [one, many] = CATEGORY_NOUNS[category];
    return `${count} ${count === 1 ? one : many}`;
  }).filter(Boolean);

export const lineSummary = (catalog, lines) => {
  const overridden = lines.filter((line) => isOverridden(catalog, line)).length;
  return [...categoryCounts(catalog, lines), overridden ? `${overridden} overridden` : null].filter(Boolean).join(" · ");
};

export const toTemplateRow = (catalog, template) => {
  const version = currentTemplateVersion(template);
  const updates = template.updates_count ?? componentUpdates(catalog, version.lines).length;
  // The list API sends counts; the rows themselves come with the template.
  const countsDetail = [
    template.earnings_count ? plural(template.earnings_count, "earning") : null,
    template.deductions_count ? plural(template.deductions_count, "deduction") : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return {
    id: template.id,
    name: template.name,
    description: template.description,
    pay_basis: template.pay_basis,
    components: template.components_count ?? (version.lines.length || (template.earnings_count || 0) + (template.deductions_count || 0)),
    components_detail: version.lines.length ? lineSummary(catalog, version.lines) : countsDetail,
    version: versionLabel(version.version),
    employees: templateEmployeeCount(template),
    updates,
    updates_label: updates ? plural(updates, "component update") : "—",
    status: templateStatus(template),
    active: template.active,
  };
};

// ---------- Form ----------

const specialAllowanceCode = (catalog) =>
  (
    catalogItem(catalog, "SPL") ||
    catalog.find((item) => item.category === "Earnings" && /special\s*allowance/i.test(item.name || ""))
  )?.code;

export const emptyTemplateForm = (catalog) => ({
  name: "",
  description: "",
  pay_basis: "Monthly",
  lines: ["BASIC", specialAllowanceCode(catalog)]
    .filter((code) => code && catalogItem(catalog, code)?.active)
    .map((code) => ({ ...lineFromComponent(catalog, code), balancing: false })),
  note: "",
});

// The line moved to its component's latest version (sent as upgrade: true).
export const upgradedLine = (catalog, line) =>
  catalogItem(catalog, line.code)
    ? { ...lineFromComponent(catalog, line.code), component_id: line.component_id ?? catalogItem(catalog, line.code)?.id, balancing: line.balancing, upgrade: true }
    : line;

// reviewCodes: rows to re-check after a Duplicate changed the pay basis.
export const templateToForm = (template, { upgradeCode, reviewCodes = [] } = {}, catalog) => ({
  name: template.name,
  description: template.description,
  pay_basis: template.pay_basis,
  lines: currentTemplateVersion(template).lines.map((line) =>
    upgradeCode && line.code === upgradeCode ? upgradedLine(catalog, line) : { ...line },
  ),
  review_codes: reviewCodes,
  note: "",
});

export const duplicateForm = (template) => ({
  name: `${template.name} (copy)`,
  description: template.description,
  pay_basis: template.pay_basis,
  lines: currentTemplateVersion(template).lines.map((line) => ({ ...line })),
  note: "",
});

const lineKey = (line) => JSON.stringify([line.component_version, ...CALC_KEYS.map((key) => String(pickCalc(line)[key]))]);

// What changed between two sets of lines, per component.
export const compareLines = (catalog, beforeLines, afterLines) => {
  const before = withBalancing(catalog, beforeLines);
  const after = withBalancing(catalog, afterLines);
  const codes = [...new Set([...after.map((line) => line.code), ...before.map((line) => line.code)])];
  return codes.map((code) => {
    const old = before.find((line) => line.code === code);
    const next = after.find((line) => line.code === code);
    let change = "Unchanged";
    if (!old) change = "Added";
    else if (!next) change = "Removed";
    else if (next.component_version > old.component_version) change = "Upgraded";
    // The balancing formula follows the other earnings by itself, so it is
    // not a change of its own.
    else if (!next.balancing && lineKey(old) !== lineKey(next)) change = "Changed";
    return {
      code,
      name: lineName(catalog, next || old),
      change,
      before: old ? describeLine(old) : "—",
      after: next ? describeLine(next) : "—",
      fromVersion: old?.component_version,
      toVersion: next?.component_version,
    };
  });
};

export const orderChanged = (beforeLines, afterLines) => {
  const common = afterLines.map((line) => line.code).filter((code) => beforeLines.some((line) => line.code === code));
  const old = beforeLines.map((line) => line.code).filter((code) => common.includes(code));
  return common.join() !== old.join();
};

export const validateTemplateForm = (catalog, form, { templates, editing, needsNote }) => {
  const errors = {};
  const name = form.name.trim();
  if (!name) errors.name = "Enter a template name";
  else if (templates.some((item) => item.id !== editing?.id && item.name.toLowerCase() === name.toLowerCase())) {
    errors.name = "A template with this name already exists";
  }
  if (!form.lines.some((line) => lineCategory(catalog, line) === "Earnings")) errors.lines = "Add at least one earning";
  const lineProblems = lineErrors(catalog, form.lines);
  if (Object.keys(lineProblems).length) errors.lineErrors = lineProblems;
  if (needsNote && !form.note.trim()) errors.note = "Enter a change note";
  return errors;
};

export { codesIn, describeCalculation, versionLabel, formatRupees };
